<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { ChampionshipRegistrationAdditionalCost } from "@/model/championship/championship-registration-additional-cost"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { RegistrationPermissions } from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, AnimateIf, injectMandatory, useHttpClient, useLoadingState } from "@/vf"
import { computed, ref } from "vue"
import { TaskViewMode } from "../../Issue/util"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
    additionalCosts: ChampionshipRegistrationAdditionalCost[]
    permissions?: RegistrationPermissions
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const saving = useLoadingState()
const policy = useRegistrationPolicy(computed(() => props.permissions), computed(() => viewMode))

// Form state for new cost
const showAddForm = ref(false)
const newCostName = ref("")
const newCostDescription = ref("")
const newCostPrice = ref<number | undefined>(undefined)
const newCostQuantity = ref(1)

// Track which cost is being deleted (for inline confirm)
const deletingCostId = ref<string | null>(null)

/*─────────────────────────────────────┐
│  Add cost                            │
└─────────────────────────────────────*/
function toggleAddForm() {
    showAddForm.value = !showAddForm.value
    if (!showAddForm.value) {
        resetForm()
    }
}

function resetForm() {
    newCostName.value = ""
    newCostDescription.value = ""
    newCostPrice.value = undefined
    newCostQuantity.value = 1
}

async function addCost() {
    if (!policy.value.CreateAdditionalCost) {
        return
    }

    if (!newCostName.value.trim() || newCostPrice.value === undefined) return

    await saving.while(async () => {
        await http.post(`/championship/registration/${props.registrationId}/team/${props.team.id}/additional-cost?viewMode=${viewMode}`, {
            name: newCostName.value,
            description: newCostDescription.value,
            price: Math.round((newCostPrice.value ?? 0) * 100), // Convert to cents
            quantity: newCostQuantity.value,
        })

        resetForm()
        showAddForm.value = false
        emit("updated")
    })
}

/*─────────────────────────────────────┐
│  Delete cost                         │
└─────────────────────────────────────*/

const deleteConfirm = createInlineConfirm({
    async action() {
        if (!deletingCostId.value) return
        await http.delete(`/championship/registration/${props.registrationId}/additional-cost/${deletingCostId.value}?viewMode=${viewMode}`)
    },
    afterAction() {
        deletingCostId.value = null
        emit("updated")
    },
})

function startDelete(cost: ChampionshipRegistrationAdditionalCost) {
    if (!policy.value.DeleteAdditionalCost) {
        return
    }

    deletingCostId.value = cost.id ?? null
    deleteConfirm.open()
}

const trans = defineTranslations({
    no_costs: {
        de: "Keine zusätzlichen Kosten vorhanden.",
        en: "No additional costs.",
    },
    from_change_request: {
        de: "Aus Änderungsanfrage",
        en: "From change request",
    },
    add_cost: {
        de: "Kosten hinzufügen",
        en: "Add cost",
    },
    cancel: {
        de: "Abbrechen",
        en: "Cancel",
    },
    name: {
        de: "Bezeichnung",
        en: "Name",
    },
    description: {
        de: "Beschreibung (optional)",
        en: "Description (optional)",
    },
    quantity: {
        de: "Menge",
        en: "Qty",
    },
    price: {
        de: "Preis",
        en: "Price",
    },
    total: {
        de: "Gesamt",
        en: "Total",
    },
    save: {
        de: "Speichern",
        en: "Save",
    },
    delete_confirmation: {
        de: "Möchtest du diese Kosten wirklich löschen?",
        en: "Do you really want to delete this cost?",
    },
    delete_success: {
        de: "Die Kosten wurden gelöscht.",
        en: "The cost has been deleted.",
    },
})
</script>

<template>
    <div class="p-3">
        <!-- Existing costs list -->
        <table class="table table-sm mb-0">
            <thead>
                <tr>
                    <th>{{ trans("quantity") }}</th>
                    <th>{{ trans("name") }}</th>
                    <th class="text-right">{{ trans("price") }}</th>
                    <th class="text-right">{{ trans("total") }}</th>
                    <th style="width: 50px"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="additionalCosts.length === 0">
                    <td colspan="5" class="text-center py-4 text-muted">
                        {{ trans("no_costs") }}
                    </td>
                </tr>
                <tr v-for="cost in additionalCosts" :key="cost.id">
                    <td class="align-middle">{{ cost.quantity }}</td>
                    <td class="align-middle">
                        <div>{{ cost.name }}</div>
                        <div v-if="cost.description" class="text-muted small">{{ cost.description }}</div>
                        <div v-if="cost.changeRequestId" class="text-info small">
                            <i class="fas fa-info-circle mr-1"></i>
                            {{ trans("from_change_request") }}
                        </div>
                    </td>
                    <td class="text-right align-middle">{{ $currency(cost.price) }}</td>
                    <td class="text-right align-middle">{{ $currency((cost.price ?? 0) * (cost.quantity ?? 1)) }}</td>
                    <td class="text-right align-middle">
                        <button
                            v-if="policy.DeleteAdditionalCost"
                            type="button"
                            class="btn btn-sm btn-danger mb-0"
                            @click="startDelete(cost)"
                        >
                            <i class="fas fa-trash"></i>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Delete confirmation -->
        <VarsityInlineConfirm :controller="deleteConfirm">
            <template #confirmation>{{ trans("delete_confirmation") }}</template>
            <template #success>{{ trans("delete_success") }}</template>
        </VarsityInlineConfirm>

        <!-- Add button -->
        <div class="mt-3" v-if="!showAddForm && policy.CreateAdditionalCost">
            <button type="button" class="btn btn-primary" @click="toggleAddForm">
                <i class="fas fa-plus mr-2"></i>
                {{ trans("add_cost") }}
            </button>
        </div>

        <!-- Add new cost form -->
        <AnimateIf :if="showAddForm && policy.CreateAdditionalCost">
            <div class="border-top pt-3 mt-3">
                <table class="table table-sm mb-3">
                    <tbody>
                        <tr>
                            <td style="width: 80px">
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model.number="newCostQuantity"
                                    min="1"
                                    step="1"
                                    style="width: 10ex"
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="newCostName"
                                    :placeholder="trans('name')"
                                    v-focus
                                />
                                <input
                                    type="text"
                                    class="form-control mt-1"
                                    v-model="newCostDescription"
                                    :placeholder="trans('description')"
                                />
                            </td>
                            <td style="width: 150px">
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model.number="newCostPrice"
                                    min="0"
                                    step="0.01"
                                    :placeholder="trans('price')"
                                    style="width: 15ex"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div class="d-flex justify-content-between">
                    <button type="button" class="btn btn-secondary" @click="toggleAddForm">
                        {{ trans("cancel") }}
                    </button>
                    <LoadingButton
                        class="btn btn-primary"
                        :loading="saving"
                        :disabled="!newCostName.trim() || newCostPrice === undefined"
                        @click="addCost"
                    >
                        {{ trans("save") }}
                    </LoadingButton>
                </div>
            </div>
        </AnimateIf>
    </div>
</template>
